import { reactive } from '@vue/composition-api'
import { uniq } from 'lodash'
import { StoreBase, ValueType } from '@/store/StoreBase'
import ContractPlanDocument, {
  PlanGroupType,
} from '@/store/stores/collectionModule/documents/plan/ContractPlanDocument'
import type { ContractType } from '@/store/stores/collectionModule/documents/organization/OrganizationDocument'
import useOrganization from '@/store/hook/useOrganization'
import useContractPlan from '@/store/hook/useContractPlan'
import useRole from '@/store/hook/useRole'
import Const from '@/util/Const'
import {
  accountPermissionsMap,
  PermissionType,
} from '@/store/stores/pageStore/common/PermissionType'
import { Response } from '@/store/stores/collectionModule/CollectionTypes'
import { RoleType } from '@/store/stores/pageStore/common/RoleType'
import { PlatformType } from '@/store/stores/collectionModule/documents/GeneralTypes'

/**
 * 有料プラン購入時の支払い方法の型
 */
export type PurchasePaymentMethodType = 'AppStore' | 'GooglePlay' | 'CreditCard'

/**
 * 契約情報の初期状態
 */
const initialState = {
  /**
   * ログインユーザーのプランが無料プランかどうか
   */
  isFreePlan: false,
  /**
   * ログインユーザーのプランが有料プランかどうか
   */
  isPaidPlan: false,
  /**
   * ログインユーザーのプランがリードオンリー状態かどうか
   */
  isReadOnlyPlan: false,
  /**
   * ログインユーザーのプランがクーポンプランかどうか
   */
  isCouponPlan: false,
  /**
   * ログインユーザーのプランがトライアルプランかどうか
   */
  isTrialPlan: false,
  /**
   * プランを登録しているかどうか
   */
  hasNoPlan: false,
  /**
   * 有効な契約情報
   */
  effectiveOrgContract: undefined as ContractType | undefined,
  /**
   * 有効なプランのプランマスタ情報
   */
  effectiveContractPlan: undefined as ContractPlanDocument | undefined,
}

/* eslint-disable class-methods-use-this */
class ContractInfoStore implements StoreBase {
  createStore() {
    const state = reactive({ ...initialState })

    // hook
    const {
      fetchContractPlans,
      fetchTargetContractPlan,
      fetchContractPlansWithCouponPlan,
      contractPlans,
      clearContractPlans,
    } = useContractPlan()
    const {
      fetchOrganizations,
      ownOrganization,
      updateOrganization,
      clearOrganizations,
      ownOrganizationPermission,
    } = useOrganization()
    const { fetchRoles, roles, clearRoles } = useRole()

    /** ログインユーザーに適応されている契約プランを判定するためのフラグをセット
     * 無料プラン
     * 有料プラン
     * クーポンプラン
     * トライアルプラン
     */
    const setLoginUserPlan = (planGroupId: PlanGroupType) => {
      switch (planGroupId) {
        // 無料プラン
        case 'freePlan':
          state.isFreePlan = true
          break
        // 有料プラン
        case 'monthlyPlan':
        case 'annualPlan':
        case Const.IN_APP_PURCHASE_SUBSC_PLAN_GROUP_ID.IOS.MONTHLY:
        case Const.IN_APP_PURCHASE_SUBSC_PLAN_GROUP_ID.IOS.ANNUAL:
        case Const.IN_APP_PURCHASE_SUBSC_PLAN_GROUP_ID.IOS.MONTHLY_TEST:
        case Const.IN_APP_PURCHASE_SUBSC_PLAN_GROUP_ID.IOS.ANNUAL_TEST:
        case Const.IN_APP_PURCHASE_SUBSC_PLAN_GROUP_ID.ANDROID.MONTHLY:
        case Const.IN_APP_PURCHASE_SUBSC_PLAN_GROUP_ID.ANDROID.ANNUAL:
          state.isPaidPlan = true
          break
        // クーポンプラン
        case 'limitedTimePaidPlan':
        case 'limitedTimePaidPlanForAnnual':
          state.isCouponPlan = true
          break
        // トライアルプラン
        case 'trialPlan':
        case 'trialPlanForAnnual':
        case Const.IN_APP_PURCHASE_SUBSC_TRIAL_PLAN_GROUP_ID.IOS.ANNUAL:
        case Const.IN_APP_PURCHASE_SUBSC_TRIAL_PLAN_GROUP_ID.IOS.MONTHLY:
        case Const.IN_APP_PURCHASE_SUBSC_TRIAL_PLAN_GROUP_ID.IOS.ANNUAL_TEST:
        case Const.IN_APP_PURCHASE_SUBSC_TRIAL_PLAN_GROUP_ID.IOS.MONTHLY_TEST:
        case Const.IN_APP_PURCHASE_SUBSC_TRIAL_PLAN_GROUP_ID.ANDROID.ANNUAL:
        case Const.IN_APP_PURCHASE_SUBSC_TRIAL_PLAN_GROUP_ID.ANDROID.MONTHLY:
          state.isTrialPlan = true
          break
        // リードオンリープラン
        case 'settlementReadonlyPlan':
          state.isReadOnlyPlan = true
          break
        default:
          break
      }
    }

    /** ユーザーに紐づく組織情報、契約情報を取得 */
    const fetchUserContractInfo = async () => {
      // stateに保持している情報をクリアする
      Object.assign(state, initialState)

      const results = []
      const fetchOrganizationsResult = await fetchOrganizations()
      if (!fetchOrganizationsResult.isSuccess) {
        return [fetchOrganizationsResult]
      }
      results.push(fetchOrganizationsResult)

      /** リードオンリープラン状態かどうかを判別する分岐処理 */
      if (ownOrganization.value?.isReadOnly) {
        state.effectiveOrgContract = ownOrganization.value?.contract?.find(
          (v) => v.planType === 'READONLY',
        )
      } else {
        /**
         * ログインユーザーに紐づいた組織情報に存在する有効な契約情報取得
         */
        state.effectiveOrgContract = ownOrganization.value?.contract?.[0]
      }

      /** クーポンプランの場合、masterContractPlanにプラン情報が入っている */
      let fetchContractPlansResult: Response<ContractPlanDocument>
      if (state.effectiveOrgContract?.masterContractPlan) {
        /**
         * クーポンプランの場合の処理
         */
        // クーポンプランの契約プランID
        const contractPlanId = state.effectiveOrgContract?.masterContractPlan.contractPlanId || ''
        // クーポンプランのプラングループID
        const couponPlanGroupId = state.effectiveOrgContract.masterContractPlan.planGroupId
        fetchContractPlansResult = await fetchContractPlansWithCouponPlan(
          contractPlanId,
          couponPlanGroupId,
        )
      } else {
        /**
         * クーポンプラン以外の場合の処理
         */
        fetchContractPlansResult = await fetchContractPlans()
      }
      if (!fetchContractPlansResult.isSuccess) {
        return [fetchContractPlansResult]
      }
      results.push(fetchContractPlansResult)

      /** 有効なプランのプランマスタ情報をセット */
      state.effectiveContractPlan = contractPlans.value.find(
        (plan) => plan.contractPlanId === state.effectiveOrgContract?.contractPlanId,
      )

      /** ログインユーザーに適応されている契約プランをセット */
      setLoginUserPlan(state.effectiveContractPlan?.planGroupId || '')

      if (!ownOrganization.value?.contract) {
        // スタッフアカウントなど契約情報がないアカウントについては有料会員扱いする。
        state.isPaidPlan = true
        // スタッフアカウントなど以外で契約情報がないにも関わらずログインできてしまうユーザー用（有料会員にアップグレードできるようにする）
        state.hasNoPlan = true
      }

      return results
    }

    /**
     * ロールマスタAPIで取得したパーミッションの内、ログインユーザーのロールと一致しているパーミッションを返す
     * @param userRoles
     */
    const getPermissionsByUserRoles = (userRoles: Array<RoleType> | undefined) =>
      uniq(
        roles.value
          .filter((role) => userRoles?.some((userRole) => userRole === role.role))
          .map((filteredRole) => filteredRole.permissions)
          .flat(),
      ) ?? []

    /**
     * 指定された権限を持っているかどうかを判定する
     * @param userRoles
     * @param permission
     */
    const hasPermission = (userRoles: Array<RoleType> | undefined, permission: PermissionType) =>
      // スタッフアカウントなど契約情報がないアカウントについては有料会員扱いする。
      ownOrganization.value?.contract
        ? [
            ...(ownOrganizationPermission.value ?? []),
            ...getPermissionsByUserRoles(userRoles),
          ]?.includes(permission)
        : [...accountPermissionsMap.staff, ...getPermissionsByUserRoles(userRoles)].includes(
            permission,
          )

    /**
     * プラングループID毎に分類された支払い方法マップを生成する
     * @param planGroupIdDefs プラングループID定義
     */
    const getPaymentMethodMapByPlanGroupId = (
      planGroupIdDefs: Record<PlatformType, Record<string, string>>,
    ): Record<string, PurchasePaymentMethodType> => {
      const map: Record<string, PurchasePaymentMethodType> = {}
      // プラットフォーム毎の固定支払い方法マップ
      const platformToPaymentMethod: Record<PlatformType, PurchasePaymentMethodType> = {
        IOS: 'AppStore',
        ANDROID: 'GooglePlay',
      }

      // IOS と ANDROID に対してループ
      Object.entries(planGroupIdDefs).forEach(([platform, plans]) => {
        const paymentMethod = platformToPaymentMethod[platform as PlatformType]

        // 各プラン（ANNUAL、MONTHLY、ANNUAL_TEST など）をループしてマップに追加
        Object.values(plans).forEach((planGroupId) => {
          map[planGroupId] = paymentMethod
        })
      })

      return map
    }

    /**
     * 有効なplanGroupIdからどの支払い方法で有料プランになったかを判定する
     */
    const getPaymentMethod = (): PurchasePaymentMethodType | null => {
      // プラングループID毎に分類されたアプリ内課金の支払い方法
      const inAppPurchasePaymentMethodsByPlanGroupId = {
        ...getPaymentMethodMapByPlanGroupId(Const.IN_APP_PURCHASE_SUBSC_PLAN_GROUP_ID),
        ...getPaymentMethodMapByPlanGroupId(Const.IN_APP_PURCHASE_SUBSC_TRIAL_PLAN_GROUP_ID),
      }

      if (state.isFreePlan) {
        // 課金ユーザーではない場合、nullを返す
        return null
      }

      /**
       * inAppPurchasePaymentMethodsByPlanGroupIdに存在するプラングループIDのプランを契約している場合: AppStore または GooglePlayを返す
       * inAppPurchasePaymentMethodsByPlanGroupIdに存在しないプラングループIDのプランを契約している場合: CreditCardを返す（=Web課金とみなす）
       */
      return (
        inAppPurchasePaymentMethodsByPlanGroupId[state.effectiveContractPlan?.planGroupId ?? ''] ??
        'CreditCard'
      )
    }

    /**
     * 契約情報をクリアする
     */
    const clearUserContractInfoData = () => {
      clearOrganizations()
      clearContractPlans()
      clearRoles()
      Object.assign(state, initialState)
    }

    return {
      get isFreePlan() {
        return state.isFreePlan
      },
      get isPaidPlan() {
        return state.isPaidPlan
      },
      get isCouponPlan() {
        return state.isCouponPlan
      },
      get isTrialPlan() {
        return state.isTrialPlan
      },
      get isReadOnlyPlan() {
        return state.isReadOnlyPlan
      },
      get hasNoPlan() {
        return state.hasNoPlan
      },
      get effectiveOrgContract() {
        return state.effectiveOrgContract || null
      },
      get effectiveContractPlan() {
        return state.effectiveContractPlan || null
      },
      get paymentMethod() {
        return getPaymentMethod()
      },
      fetchUserContractInfo,
      fetchTargetContractPlan,
      fetchOrganizations,
      contractPlans, // クーポン適用中のユーザーでログインした場合はクーポンプラン情報も含まれている
      ownOrganization,
      fetchRoles,
      clearUserContractInfoData,
      hasPermission,
      updateOrganization,
    }
  }
}
const value: ValueType<ContractInfoStore> = {}

export default {
  createStore: new ContractInfoStore().createStore,
  value: value as Required<typeof value>,
}
